import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Link, useLocation } from "react-router-dom";
import "components/layouts/Sidebar.css";

const ModuleSidebar = ({ collapsed, onToggle, menuItems = [] }) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState(() => new Map());

  const isMenuItemActive = useCallback(
    (path) =>
      location.pathname === path ||
      (path !== "/contracts" && location.pathname.startsWith(`${path}/`)) ||
      (path === "/contracts" &&
        location.pathname.match(/^\/contracts\/(?!overview$|calendar$|notifications$|items$|history$)[^/]+$/)) ||
      (path === "/subcontracts/contracts" &&
        location.pathname.startsWith("/subcontracts/contracts/")) ||
      (["/subcontracts/payments", "/subcontracts/acceptances", "/subcontracts/approvals"].includes(path) &&
        location.pathname.startsWith(`${path}/`)) ||
      (path === "/equipment" && location.pathname.startsWith("/equipment/")),
    [location.pathname],
  );

  const normalizedMenuItems = useMemo(
    () =>
      menuItems.map((item, index) => ({
        ...item,
        key: item.key || item.path || `${item.label}-${index}`,
      })),
    [menuItems],
  );

  const hasActiveChild = useCallback(
    (item) => item.children?.some((child) => isMenuItemActive(child.path)),
    [isMenuItemActive],
  );

  useEffect(() => {
    setExpandedGroups((current) => {
      const activeGroup = normalizedMenuItems.find((item) => item.children && hasActiveChild(item));
      if (!activeGroup || (current.size === 1 && current.get(activeGroup.key))) {
        return current;
      }

      return new Map([[activeGroup.key, true]]);
    });
  }, [hasActiveChild, location.pathname, normalizedMenuItems]);

  const toggleGroup = (key) => {
    setExpandedGroups((current) => {
      if (current.get(key)) {
        return new Map();
      }

      return new Map([[key, true]]);
    });
  };

  const renderMenuLink = (item, level = 0) => (
    <div className={`sidebar-direct-item ${isMenuItemActive(item.path) ? "active" : ""}`}>
      <Link to={item.path} className="sidebar-link" title={collapsed ? item.label : ""}>
        <div className={`sidebar-item-left level-${level}`}>
          <span className="sidebar-icon">{item.icon}</span>
          {!collapsed && <span className="sidebar-text">{item.label}</span>}
        </div>
      </Link>
    </div>
  );

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <button
        type="button"
        className="module-sidebar-toggle subcontract-sidebar-toggle"
        onClick={onToggle}
        aria-label={collapsed ? "Mở rộng thanh điều hướng" : "Thu gọn thanh điều hướng"}
        title={collapsed ? "Mở rộng" : "Thu gọn"}
      >
        {collapsed ? <ChevronRightIcon fontSize="inherit" /> : <ChevronLeftIcon fontSize="inherit" />}
      </button>

      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img src="/logoXNTD.png" alt="Logo Xí nghiệp Trắc địa" className="sidebar-logo-img" />
          {!collapsed && (
            <div className="sidebar-title">
              <span className="sidebar-title-sub">Hệ thống quản lý</span>
              <span className="sidebar-title-sub">Xí nghiệp Trắc địa</span>
            </div>
          )}
        </div>
      </div>

      <nav className="sidebar-nav">
        {normalizedMenuItems.map((item) => (
          <div key={item.key} className="sidebar-group">
            {item.children ? (
              <>
                <button
                  type="button"
                  className={`sidebar-group-button ${expandedGroups.get(item.key) ? "expanded" : ""}`}
                  onClick={() => toggleGroup(item.key)}
                  title={collapsed ? item.label : ""}
                >
                  <div className="sidebar-item-left level-0">
                    {item.icon && <span className="sidebar-icon">{item.icon}</span>}
                    {!collapsed && <span className="sidebar-text">{item.label}</span>}
                  </div>
                  <span className="sidebar-expand-icon">
                    <KeyboardArrowRightIcon fontSize="inherit" />
                  </span>
                </button>

                {expandedGroups.get(item.key) === true && (
                  <ul className="sidebar-submenu">
                    {item.children.map((child) => (
                      <li
                        key={child.path}
                        className={isMenuItemActive(child.path) ? "sidebar-submenu-item active" : "sidebar-submenu-item"}
                      >
                        {renderMenuLink(child, 1)}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              renderMenuLink(item)
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default ModuleSidebar;
